import React, { useContext, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { Link, useLocation } from 'react-router-dom';
import ProfileContext from '../context/ProfileContext';
import AuthContext from '../context/AuthContext';
import { fetchNotificationCount, fetchProfilePicture } from '../api';

const DEFAULT_GYM_PROFILE = '/images/default-gym-profile.png';

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { profile } = useContext(ProfileContext);
  const { authTokens, logoutUser } = useContext(AuthContext);
  const accessToken = authTokens?.access;
  const [profilePic, setProfilePic] = useState(DEFAULT_GYM_PROFILE);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    const loadPicture = async () => {
      if (!profile?.profile) {
        setProfilePic(DEFAULT_GYM_PROFILE);
        return;
      }
      try {
        const url = await fetchProfilePicture(profile.profile);
        if (active) setProfilePic(url || DEFAULT_GYM_PROFILE);
      } catch (_) {
        if (active) setProfilePic(DEFAULT_GYM_PROFILE);
      }
    };
    loadPicture();
    return () => { active = false; };
  }, [profile?.profile]);

  useEffect(() => {
    if (!accessToken) {
      setNotificationCount(0);
      return undefined;
    }

    const loadCount = async () => {
      try {
        setNotificationCount(await fetchNotificationCount({ access: accessToken }));
      } catch (_) {
        setNotificationCount(0);
      }
    };
    loadCount();
    const handleCommentsUpdate = (event) => {
      if (typeof event.detail?.unseenComments === 'number') {
        setNotificationCount(event.detail.unseenComments);
      } else {
        loadCount();
      }
    };
    window.addEventListener('gyma:comments-updated', handleCommentsUpdate);
    return () => window.removeEventListener('gyma:comments-updated', handleCommentsUpdate);
  }, [accessToken]);

  const menuItems = [
    { text: 'Accueil', path: '/home' },
    { text: 'Abonnements', path: '/memberships' },
    { text: 'Commentaires', path: '/comments', badge: notificationCount },
    { text: 'Profil', path: '/profile' },
  ];

  const navButton = (item) => (
    <Button
      key={item.path}
      component={Link}
      to={item.path}
      color="inherit"
      sx={{
        borderRadius: 2,
        px: 1.6,
        fontWeight: 750,
        bgcolor: location.pathname === item.path ? 'rgba(255,255,255,0.14)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.11)' },
      }}
    >
      {item.badge ? (
        <Badge badgeContent={item.badge} color="error" sx={{ pr: 0.5 }}>
          {item.text}
        </Badge>
      ) : item.text}
    </Button>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: '#000449',
          backgroundImage: 'linear-gradient(90deg, #000449 0%, #101A63 100%)',
          boxShadow: '0 8px 24px rgba(0,4,73,0.2)',
        }}
      >
        <Toolbar sx={{ minHeight: 70, gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="Ouvrir le menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box component={Link} to="/home" sx={{ color: 'inherit', textDecoration: 'none' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AllInclusiveRoundedIcon sx={{ color: '#F95A00', fontSize: 34 }} />
              <Typography variant="h6" fontWeight={950}>Gyma Pro</Typography>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}
          >
            {menuItems.map(navButton)}
          </Stack>

          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ ml: 'auto' }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography variant="body2" fontWeight={800}>{profile?.name || 'Ma salle'}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.72 }}>Espace propriétaire</Typography>
            </Box>
            <Tooltip title="Voir le profil">
              <Avatar
                component={Link}
                to="/profile"
                src={profilePic}
                imgProps={{
                  onError: (event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_GYM_PROFILE;
                  },
                }}
                sx={{ width: 42, height: 42, border: '2px solid rgba(255,255,255,0.9)' }}
              />
            </Tooltip>
            <Tooltip title="Se déconnecter">
              <IconButton color="inherit" onClick={logoutUser} aria-label="Se déconnecter">
                <LogoutRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, p: 2 }} role="presentation">
          <Stack direction="row" spacing={1} alignItems="center" py={1.5}>
            <AllInclusiveRoundedIcon sx={{ color: '#F95A00', fontSize: 34 }} />
            <Box>
              <Typography fontWeight={950}>Gyma Pro</Typography>
              <Typography variant="caption" color="text.secondary">{profile?.name || 'Ma salle'}</Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <List>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.path}
                component={Link}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText primary={item.text} />
                {item.badge ? <Badge badgeContent={item.badge} color="error" /> : null}
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
